import React from "react";
import {
  Box,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";

const VIS = [
  { value: "publico", label: "Público" },
  { value: "oculto", label: "Oculto (não aparece)" },
];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    backgroundColor: "rgba(255,255,255,0.03)",
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.2)" },
    "&.Mui-focused fieldset": { borderColor: "rgba(0,225,255,0.55)" },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.58)" },
  "& .MuiInputBase-input": { color: "#fff" },
  "& input[type='date']::-webkit-calendar-picker-indicator": {
    filter: "invert(1)",
    opacity: 0.7,
  },
};

export default function TicketEditor({
  ticket,
  index,
  onChange,
  onRemove,
  errors,
}) {
  const getErr = (field) => errors?.[`ingressos.${index}.${field}`];

  return (
    <Box
      sx={{
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 2,
        p: { xs: 1.5, md: 2 },
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))",
      }}
    >
      <Stack spacing={1.6}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <Chip
              icon={<ConfirmationNumberRoundedIcon sx={{ fontSize: 16 }} />}
              label={`Ingresso ${index + 1}`}
              sx={{
                fontWeight: 900,
                bgcolor: "rgba(0,225,255,0.12)",
                color: "#8ef4ff",
                border: "1px solid rgba(0,225,255,0.2)",
              }}
            />
            <Chip size="small" label={ticket.visibilidade === "oculto" ? "Oculto" : "Público"} sx={{ fontWeight: 800 }} />
          </Stack>

          <IconButton
            onClick={onRemove}
            sx={{
              border: "1px solid rgba(255,255,255,0.1)",
              color: "rgba(255,255,255,0.58)",
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Grid container spacing={1.6}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nome do ingresso"
              value={ticket.nome}
              onChange={(e) => onChange({ nome: e.target.value })}
              error={!!getErr("nome")}
              helperText={getErr("nome") || "Ex: VIP, Pista, Camarote, Promocional"}
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Lote"
              value={ticket.lote}
              onChange={(e) => onChange({ lote: e.target.value })}
              placeholder="Lote 1"
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Visibilidade"
              value={ticket.visibilidade}
              onChange={(e) => onChange({ visibilidade: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            >
              {VIS.map((v) => (
                <MenuItem key={v.value} value={v.value}>
                  {v.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Preço (R$)"
              value={ticket.preco}
              onChange={(e) => onChange({ preco: Number(e.target.value || 0) })}
              error={!!getErr("preco")}
              helperText={getErr("preco") || "Preço base exibido para o público."}
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Quantidade"
              value={ticket.quantidade}
              onChange={(e) => onChange({ quantidade: Number(e.target.value || 0) })}
              error={!!getErr("quantidade")}
              helperText={getErr("quantidade") || "Total disponível neste ingresso."}
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Box
              sx={{
                height: "100%",
                minHeight: 56,
                display: "flex",
                alignItems: "center",
                px: 1.4,
                borderRadius: 2,
                border: "1px solid rgba(255,255,255,0.12)",
                bgcolor: "rgba(255,255,255,0.03)",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={!!ticket.taxaAbsorvidaOrganizador}
                    onChange={(e) => onChange({ taxaAbsorvidaOrganizador: e.target.checked })}
                  />
                }
                label="Taxa ONURB absorvida pelo organizador"
                sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 13, color: "rgba(255,255,255,0.8)" } }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Início das vendas (opcional)"
              InputLabelProps={{ shrink: true }}
              value={ticket.vendas?.inicio || ""}
              onChange={(e) => onChange({ vendas: { ...ticket.vendas, inicio: e.target.value } })}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Fim das vendas (opcional)"
              InputLabelProps={{ shrink: true }}
              value={ticket.vendas?.fim || ""}
              onChange={(e) => onChange({ vendas: { ...ticket.vendas, fim: e.target.value } })}
              sx={inputSx}
            />
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
}
